import { useState, useEffect, useRef } from "react";
import { catchErrorLog } from "@util/sentry";
import StarRating from "@component/star-rating";
import { Button } from "@component/button";
import { Textarea } from "@component/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@component/select";
import { sendInternalInterviewCompleteNotification } from "@service/interview";
import { sendTrainingSessionFeedback } from "@service/training";
import { UAParser } from 'ua-parser-js';
import { useParams } from "react-router-dom";

interface FeedbackPopupProps {
    aiName?: string;
    onComplete?: () => void;
    className?: string;
    interviewStartTime: number; // Timestamp when interview started (in milliseconds)
    userId: string;
    conversationId: string;
    interviewName?: string
    assessmentId?: string;
    selectedAudioDeviceID?: string;
    selectedVideoDeviceID?: string;
    interviewLanguage?: string
    hasFormAssessment?: boolean;
    hasEligibilityCheck?: boolean;
    // "roleplay" swaps all visible "interview" copy to "roleplay" equivalents.
    mode?: "interview" | "roleplay";
    // Training session token — when provided, feedback is sent to the
    // training-specific endpoint instead of the recruiter interview one.
    sessionToken?: string;
}

export default function FeedbackPopup({
    onComplete,
    className,
    interviewStartTime,
    userId,
    conversationId,
    interviewName = "First Time Interview",
    assessmentId,
    selectedAudioDeviceID = "default",
    selectedVideoDeviceID = "default",
    interviewLanguage,
    hasFormAssessment = false,
    hasEligibilityCheck = false,
    mode = "interview",
    sessionToken,
}: FeedbackPopupProps) {
    const isRoleplay = mode === "roleplay";
    // Store important props in refs to ensure they're preserved
    const userIdRef = useRef(userId);
    const conversationIdRef = useRef(conversationId);
    const interviewStartTimeRef = useRef(interviewStartTime || Date.now());
    const { companyname } = useParams()
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState("");
    const [selectedIssue, setSelectedIssue] = useState<string>("none");
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Check if Indonesian translation is needed
    const isIndonesian = interviewLanguage === "id-ID";

    // Scenario B: AI interview + Form/Eligibility Check
    const totalSteps = 1 + (hasFormAssessment ? 1 : 0) + (hasEligibilityCheck ? 1 : 0);
    const isScenarioB = totalSteps >= 2;

    // Translation labels
    const labels = {
        title: isIndonesian ? "Bagaimana pengalaman wawancara Anda?" : (isRoleplay ? "How was your roleplay experience?" : "How was your interview experience?"),
        subtitle: isScenarioB
            ? (isIndonesian
                ? <>Luangkan waktu untuk menilai pengalaman Anda.<br />Setelah dikirim, Anda akan melanjutkan ke langkah berikutnya dari skrining Anda.</>
                : <>Take a moment to rate your experience.<br />Once submitted, you'll continue to the next steps of your screening.</>)
            : (isIndonesian
                ? "Masukan Anda membantu kami meningkatkan pengalaman wawancara AI."
                : (isRoleplay ? "Your feedback helps us improve our AI roleplay experience." : "Your feedback helps us improve our AI interview experience.")),
        issuesQuestion: isIndonesian ? "Apakah Anda mengalami kendala?" : "Did you face any issues?",
        selectPlaceholder: isIndonesian ? "Pilih kendala (jika ada)" : "Select an issue (if any)",
        none: isIndonesian ? "Tidak ada" : "None",
        feedbackPlaceholder: isIndonesian
            ? "Bagikan masukan tambahan tentang pengalaman wawancara Anda (opsional)"
            : (isRoleplay ? "Share any additional feedback about your roleplay experience (optional)" : "Share any additional feedback about your interview experience (optional)"),
        submitButton: isScenarioB
            ? (isIndonesian ? "Kirim dan Lanjutkan →" : "Submit and Continue →")
            : (isIndonesian ? "Kirim Masukan" : "Submit Feedback"),
        submitting: isIndonesian ? "Mengirim..." : "Submitting...",
        // Issue options
        transcriptionIssues: isIndonesian ? "Masalah transkripsi" : "Transcription issues",
        interviewerIssues: isIndonesian ? "Masalah pewawancara" : (isRoleplay ? "Roleplay coach issues" : "Interviewer issues"),
        deviceIssues: isIndonesian ? "Masalah mikrofon atau kamera" : (isRoleplay ? "Mic issues" : "Mic or Camera issues"),
        other: isIndonesian ? "Masalah lainnya" : "Other issues",
        // Shown under the comment box once an issue is selected but the user
        // hasn't written an explanation yet.
        detailsRequiredHint: isIndonesian
            ? "Mohon jelaskan kendala Anda agar tim kami dapat menindaklanjutinya."
            : "Please describe the issue so our team can look into it.",
    };

    // Per-issue example prompts for the comment box. A concrete example nudges
    // candidates to write something support can actually act on.
    const issuePlaceholders: Record<string, string> = {
        transcription: isIndonesian
            ? "Contoh: Teks yang ditampilkan tidak sesuai dengan apa yang saya ucapkan."
            : "e.g. The on-screen captions didn't match what I actually said.",
        interviewer: isIndonesian
            ? "Contoh: Pewawancara AI tidak merespons atau terus mengulang pertanyaan yang sama."
            : "e.g. The AI interviewer didn't respond, or kept repeating the same question.",
        device: isIndonesian
            ? "Contoh: Mikrofon saya tidak menangkap suara / kamera menampilkan layar hitam."
            : "e.g. My mic didn't pick up my voice / my camera showed a black screen.",
        other: isIndonesian
            ? "Contoh: Jelaskan kendala yang Anda alami selama wawancara."
            : "e.g. Tell us what went wrong during your interview.",
    };

    // Update refs when props change - only update if the new value is not null
    useEffect(() => {
        if (userId) userIdRef.current = userId;
        if (conversationId) conversationIdRef.current = conversationId;
        // Only update start time if it's provided and we don't already have one
        if (interviewStartTime && !interviewStartTimeRef.current) {
            interviewStartTimeRef.current = interviewStartTime;
        }

        console.log("FeedbackPopup props updated:", {
            conversationId: conversationId,
            currentRefConversationId: conversationIdRef.current,
            userId: userId,
            interviewStartTime: interviewStartTime
        });
    }, [userId, conversationId, interviewStartTime]);

    // Initial load logging
    useEffect(() => {
        console.log("FeedbackPopup initialized with props:", {
            conversationId: conversationId,
            userId: userId,
            interviewStartTime: interviewStartTime
        });
    }, []);

    // Add a debugging effect to monitor any conversation ID changes from parent
    useEffect(() => {
        if (conversationId) {
            console.log("ConversationId provided by parent:", conversationId);
        }
    }, [conversationId]);

    const issueOptions = [
        { value: "transcription", label: labels.transcriptionIssues },
        { value: "interviewer", label: labels.interviewerIssues },
        { value: "device", label: labels.deviceIssues },
        { value: "other", label: labels.other }
    ];

    // Any selected issue (i.e. anything other than "none") now requires a
    // written explanation before the form can be submitted.
    const detailsRequired = selectedIssue !== "none";
    const detailsMissing = detailsRequired && comment.trim().length === 0;

    const handleFeedbackSubmit = async () => {
        // Block submission until the user explains a selected issue.
        if (detailsMissing) return;
        // Log the current values before submission
        console.log("Submitting feedback with:", {
            userId: userIdRef.current,
            conversationId: conversationIdRef.current,
            interviewStartTime: interviewStartTimeRef.current
        });
        console.log(companyname)
        // For the training flow (sessionToken present) we don't need companyname/userId.
        // For the recruiter interview flow, bail if we're missing the required context.
        if (!sessionToken && !companyname && (!userIdRef.current || !conversationIdRef.current)) {
            console.error("Missing essential data for feedback submission:", {
                userId: userIdRef.current,
                conversationId: conversationIdRef.current
            });
            setIsSubmitting(false);
            onComplete?.();
            return;
        }

        setIsSubmitting(true);
        try {
            const listDevices = await navigator.mediaDevices.enumerateDevices();
            const audioInputDevices = listDevices.filter(device => device.kind === 'audioinput');
            const videoInputDevices = listDevices.filter(device => device.kind === 'videoinput');

            const currentAudioInput = audioInputDevices.find(device => device.deviceId.includes(selectedAudioDeviceID));
            const currentVideoInput = videoInputDevices.find(device => device.deviceId.includes(selectedVideoDeviceID));

            const { browser, cpu, device, os } = UAParser(navigator.userAgent);
            const interviewDuration = Math.floor((Date.now() - interviewStartTimeRef.current) / 1000);
            // Express the interview duration in MINUTES (rounded), floored to at least 1
            // so a real (sub-minute) session is never serialized as a misleading "0 min".
            const durationStr = `${Math.max(1, Math.round(interviewDuration / 60))} min`;

            const metadata = {
                "browser": browser.name || "Unknown",
                "browser_version": browser.version || "Unknown",
                "cpu_architecture": cpu.architecture || "Unknown",
                "device_model": device.model || "Unknown",
                "device_vendor": device.vendor || "Unknown",
                "device_type": device.type || "Unknown",
                "os": os.name || "Unknown",
                "audio_device": currentAudioInput,
                "video_device": currentVideoInput,
            };

            if (sessionToken) {
                await sendTrainingSessionFeedback(sessionToken, {
                    rating,
                    category: selectedIssue,
                    feedback: comment,
                    interview_duration: durationStr,
                    conversation_id: conversationIdRef.current,
                    metadata,
                });
            } else {
                await sendInternalInterviewCompleteNotification({
                    user_id: userIdRef.current,
                    conversation_id: conversationIdRef.current,
                    interview_duration: durationStr,
                    assessment_id: assessmentId,
                    feedback_data: {
                        "rating": rating,
                        "feedback": comment,
                        "category": selectedIssue,
                        "interview_name": interviewName,
                        "metadata": metadata,
                    },
                });
            }
        } catch (error) {
            console.error("Error submitting feedback:", error);
            // Console-only until now, and onComplete() below runs regardless, so
            // the flow always advances as success. Two things are lost together:
            // the candidate's rating/feedback, and the interview-complete
            // notification that tells HR the interview finished at all.
            // The raw error is deliberately NOT interpolated: catchErrorLog drops
            // any message containing 401/404, and "Request failed with status
            // code 401" is exactly what a dead session produces here. The detail
            // stays on the console; the event itself must survive.
            catchErrorLog(
                `[interview] feedback submit failed (conversation ${conversationIdRef.current || "unknown"})`,
            );
        } finally {
            setIsSubmitting(false);
        }

        // Trigger the onComplete callback after a short delay
        if (onComplete) {
            onComplete();
        }
    };

    const isFirstMoveInterview = interviewName === "First Move AI Investor";

    return (
        <div
            className={`rounded-lg border border-gray-100 bg-white p-8 shadow-sm w-full max-w-2xl mx-auto ${className}`}
        >
            <div className="space-y-4">
                <div className="text-center">
                    {isScenarioB && (
                        <div className="flex justify-center mb-4">
                            <span className="px-3.5 py-1 rounded-full bg-blue-100 text-blue-600 text-xs font-medium">
                                {totalSteps - 1} of {totalSteps} steps remaining
                            </span>
                        </div>
                    )}
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                        {labels.title}
                    </h3>
                    <p className="text-gray-600 mb-4">
                        {labels.subtitle}
                    </p>

                    <div className="flex justify-center mb-6">
                        <StarRating size="lg" onChange={setRating} initialRating={rating} />
                    </div>

                    <div className="mb-6">
                        <h4
                            className="text-base font-medium text-gray-800 mb-2"
                            style={{
                                marginTop: "10px",
                                paddingTop: "0px",
                                paddingBottom: "0px",
                            }}
                        >
                            {labels.issuesQuestion}
                        </h4>
                        <div>
                            <Select value={selectedIssue} onValueChange={setSelectedIssue}>
                                <SelectTrigger className="w-full">
                                    <SelectValue placeholder={labels.selectPlaceholder} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">{labels.none}</SelectItem>
                                    {issueOptions.map((issue) => (
                                        <SelectItem key={issue.value} value={issue.value}>
                                            {issue.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="space-y-1 mb-4 text-left">
                        <Textarea
                            placeholder={
                                detailsRequired
                                    ? issuePlaceholders[selectedIssue]
                                    : labels.feedbackPlaceholder
                            }
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            className={`min-h-[100px] ${detailsMissing ? "border-red-400 focus-visible:ring-red-400" : ""}`}
                        />
                        {detailsRequired && (
                            <p className={`text-xs ${detailsMissing ? "text-red-600" : "text-gray-500"}`}>
                                {labels.detailsRequiredHint}
                            </p>
                        )}
                    </div>

                    <Button
                        variant="default"
                        onClick={handleFeedbackSubmit}
                        disabled={rating === 0 || isSubmitting || detailsMissing}
                        className={`w-full ${isFirstMoveInterview ? "bg-black hover:bg-black" : ""}`}
                    >
                        {isSubmitting ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                {labels.submitting}
                            </>
                        ) : (
                            labels.submitButton
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
}
